import { useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fileUrl, STATUS } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Spinner } from "@/components/States";
import MoneyInput from "@/components/MoneyInput";
import DatePicker from "@/components/DatePicker";

const EMPTY = { name: "", client_name: "", client_email: "", address: "", status: "lead", start_date: "", end_date: "", budget: "", description: "", hero_image: null };

const F = ({ label, id, children }) => <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label>{children}</div>;

export default function ProjectForm({ initial, onSubmit, submitLabel = "Save project" }) {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const creating = !initial;
  const { data: tplData } = useFetch(creating ? "/workspace/prestart-templates" : null);
  const templates = tplData?.templates || [];
  const [picked, setPicked] = useState([]);
  const [heroBusy, setHeroBusy] = useState(false);
  const togglePick = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setHero = async (file) => {
    if (!file) return;
    setHeroBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "hero");
      if (initial?.id) fd.append("project_id", initial.id);
      const { data } = await api.post("/files", fd);
      setF((p) => ({ ...p, hero_image: data.url }));
    } catch { toast.error("Upload failed — images must be under 15 MB."); }
    setHeroBusy(false);
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { name: f.name, client_name: f.client_name, client_email: f.client_email, address: f.address, status: f.status,
      start_date: f.start_date || null, end_date: f.end_date || null, budget: f.budget === "" || f.budget == null ? null : Number(f.budget),
      description: f.description, hero_image: f.hero_image || null, in_lookbook: f.in_lookbook !== false, client_view: f.client_view || null, binder: f.binder || null,
      ...(creating && picked.length ? { prestart_template_ids: picked } : {}) };
    try { await onSubmit(payload); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="project-form">
      <div className="sm:col-span-2"><F label="Project name" id="pf-name"><Input id="pf-name" required value={f.name} onChange={set("name")} data-testid="project-name-input" /></F></div>
      <F label="Client name" id="pf-cn"><Input id="pf-cn" value={f.client_name} onChange={set("client_name")} data-testid="project-client-name-input" /></F>
      <F label="Client email" id="pf-ce"><Input id="pf-ce" type="email" value={f.client_email} onChange={set("client_email")} data-testid="project-client-email-input" /></F>
      <div className="sm:col-span-2"><F label="Address" id="pf-ad"><Input id="pf-ad" value={f.address} onChange={set("address")} data-testid="project-address-input" /></F></div>
      <F label="Status" id="pf-st">
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
          <SelectTrigger id="pf-st" data-testid="project-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`status-option-${k}`}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </F>
      <F label="Budget (CAD)" id="pf-b"><MoneyInput id="pf-b" value={f.budget === "" ? null : f.budget} onChange={(v) => setF({ ...f, budget: v })} placeholder="e.g. 184,500" data-testid="project-budget-input" /></F>
      <F label="Start date" id="pf-sd"><DatePicker value={f.start_date || ""} onChange={(v) => setF({ ...f, start_date: v })} placeholder="Pick a date" testid="project-start-input" /></F>
      <F label="End date" id="pf-ed"><DatePicker value={f.end_date || ""} onChange={(v) => setF({ ...f, end_date: v })} placeholder="Pick a date" testid="project-end-input" /></F>
      <div className="sm:col-span-2"><F label="Description" id="pf-d"><Textarea id="pf-d" rows={3} value={f.description} onChange={set("description")} data-testid="project-description-input" /></F></div>
      <div className="sm:col-span-2 space-y-1.5">
        <Label>Hero image <span className="font-normal text-muted-foreground">(optional)</span></Label>
        {f.hero_image ? (
          <div className="relative w-full max-w-sm">
            <img src={fileUrl(f.hero_image)} alt="Hero" className="aspect-video w-full rounded-lg object-cover" data-testid="project-hero-preview" />
            <button type="button" onClick={() => setF({ ...f, hero_image: null })} className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80" data-testid="project-hero-remove"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm text-muted-foreground hover:bg-secondary" data-testid="project-hero-upload-label">
            {heroBusy ? <Spinner /> : <ImagePlus className="h-4 w-4" />} {heroBusy ? "Uploading…" : "Upload a hero image"}
            <input type="file" accept="image/*" hidden onChange={(e) => setHero(e.target.files[0])} data-testid="project-hero-file-input" />
          </label>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={f.in_lookbook !== false} onChange={(e) => setF({ ...f, in_lookbook: e.target.checked })} data-testid="project-lookbook-checkbox" />Show in our public lookbook once complete</label>
      {creating && templates.length > 0 && (
        <fieldset className="rounded-lg border p-3 sm:col-span-2" data-testid="project-template-picker">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pre-start planning to start with</legend>
          <ul className="mt-1 space-y-1.5">
            {templates.map((t) => (
              <li key={t.id}><label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={t.auto_apply || picked.includes(t.id)} disabled={t.auto_apply} onChange={() => togglePick(t.id)} data-testid={`project-template-${t.id}`} />
                <span>{t.name}<span className="ml-1.5 text-xs text-muted-foreground">{t.items.length} items{t.auto_apply ? " · added to every new project" : ""}</span></span>
              </label></li>
            ))}
          </ul>
        </fieldset>
      )}
      <div className="sm:col-span-2"><Button type="submit" disabled={busy} className="rounded-full" data-testid="project-form-submit">{busy && <Spinner />} {submitLabel}</Button></div>
    </form>
  );
}
